import React, { useState, useEffect, useMemo } from 'react';
import { Search, Building2, Trash2, Loader2, Plus, Info } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/use-toast';
import { fetchLaundries } from '@/pages/parametrizacion/laundries/Services/laundries.services';
import { asignarCentrosUsuario } from '@/pages/parametrizacion/usuarios/Services/users.services';

/**
 * Centros de lavado que un usuario puede consultar en los reportes.
 *
 * Sin centros asignados el usuario ve todos los centros activos (que es el
 * comportamiento de siempre). Al asignarle centros, los selectores de reportes
 * quedan acotados a esa lista.
 *
 * Nota: esto acota la interfaz, no la API.
 */
const AsignarCentrosUsuarioModal = ({ isOpen, onClose, usuario, onSuccess }) => {
  const { toast } = useToast();
  const [allLaundries, setAllLaundries] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [asignados, setAsignados] = useState([]);

  useEffect(() => {
    if (isOpen && usuario) {
      setAsignados(usuario.centrosAsignados || []);
      setSearch('');
      loadLaundries();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, usuario]);

  const loadLaundries = async () => {
    setIsLoading(true);
    try {
      const res = await fetchLaundries({ limit: 500, offset: 1, query: { estado: 'Activo' } });
      setAllLaundries(res.data || []);
    } catch {
      toast({ title: 'Error', description: 'No se pudieron cargar los centros.', variant: 'destructive' });
    } finally {
      setIsLoading(false);
    }
  };

  const disponibles = useMemo(() => {
    const asignadosIds = new Set(asignados.map((c) => String(c.id)));
    return allLaundries.filter((l) => {
      const noAsignado = !asignadosIds.has(String(l._id));
      const coincide =
        !search ||
        l.nombre?.toLowerCase().includes(search.toLowerCase()) ||
        l.ciudad?.toLowerCase().includes(search.toLowerCase());
      return noAsignado && coincide;
    });
  }, [allLaundries, asignados, search]);

  const handleAgregar = (l) => {
    setAsignados((prev) => [...prev, { id: l._id, nombre: l.nombre }]);
  };

  const handleRemover = (id) => {
    setAsignados((prev) => prev.filter((c) => String(c.id) !== String(id)));
  };

  const handleGuardar = async () => {
    setIsSaving(true);
    try {
      await asignarCentrosUsuario(usuario._id, asignados);
      toast({
        title: 'Centros actualizados',
        description: asignados.length
          ? `${usuario.nombre} verá ${asignados.length} centro(s) en los reportes.`
          : `${usuario.nombre} podrá ver todos los centros.`,
      });
      onSuccess?.();
      onClose();
    } catch (e) {
      toast({
        title: 'Error',
        description: e.message || 'No se pudo guardar la asignación.',
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-5xl w-full max-h-[90vh] flex flex-col overflow-hidden p-0">
        <DialogHeader className="px-6 pt-6 pb-4 border-b border-border shrink-0">
          <DialogTitle className="flex items-center gap-2 text-lg">
            <Building2 className="h-5 w-5 text-primary" />
            Centros asignados — <span className="text-primary">{usuario?.nombre}</span>
          </DialogTitle>
          <p className="text-sm text-muted-foreground mt-1">
            Limita los centros que el usuario puede consultar en los reportes.
          </p>
        </DialogHeader>

        <div className="px-6 py-3 shrink-0">
          <div className="flex items-start gap-2 rounded-md border border-sky-500/30 bg-sky-500/10 px-3 py-2 text-xs text-sky-700 dark:text-sky-300">
            <Info className="h-4 w-4 shrink-0 mt-0.5" />
            <span>
              Sin centros asignados el usuario ve <strong>todos</strong> los
              centros activos.
            </span>
          </div>
        </div>

        <div className="flex flex-1 gap-0 overflow-hidden min-h-0">
          {/* Panel izquierdo: centros disponibles */}
          <div className="flex flex-col w-1/2 border-r border-border overflow-hidden">
            <div className="px-5 pt-2 pb-3 shrink-0">
              <p className="text-sm font-medium mb-2">
                Centros disponibles
                <span className="ml-2 text-xs text-muted-foreground font-normal">
                  ({disponibles.length})
                </span>
              </p>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <input
                  type="text"
                  placeholder="Buscar por nombre o ciudad..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-full pl-9 pr-4 py-2 rounded-md border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
                />
              </div>
            </div>

            <div className="flex-1 overflow-y-auto px-5 pb-4 custom-scrollbar">
              {isLoading ? (
                <div className="flex items-center justify-center h-32 text-sm text-muted-foreground">
                  <Loader2 className="h-5 w-5 animate-spin mr-2" /> Cargando centros...
                </div>
              ) : disponibles.length === 0 ? (
                <p className="text-sm text-muted-foreground italic text-center mt-8">
                  {allLaundries.length === 0
                    ? 'Sin centros activos en el sistema'
                    : 'Todos los centros ya están asignados o no coincide la búsqueda'}
                </p>
              ) : (
                <ul className="space-y-1">
                  {disponibles.map((l) => (
                    <li key={l._id}>
                      <button
                        onClick={() => handleAgregar(l)}
                        className="w-full text-left px-3 py-2.5 rounded-md border border-border/50 hover:border-primary/50 hover:bg-primary/5 transition-colors flex items-center justify-between group"
                      >
                        <div className="flex flex-col min-w-0">
                          <span className="text-sm font-medium truncate">{l.nombre}</span>
                          <span className="text-xs text-muted-foreground truncate">
                            {l.ciudad || ''}
                          </span>
                        </div>
                        <Plus className="h-4 w-4 text-primary shrink-0 opacity-0 group-hover:opacity-100 transition-opacity ml-2" />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>

          {/* Panel derecho: centros asignados */}
          <div className="flex flex-col w-1/2 overflow-hidden">
            <div className="px-5 pt-2 pb-3 shrink-0">
              <p className="text-sm font-medium mb-2">
                Asignados al usuario
                <span className="ml-2 text-xs text-muted-foreground font-normal">
                  ({asignados.length})
                </span>
              </p>
            </div>

            <div className="flex-1 overflow-y-auto px-5 pb-4 custom-scrollbar">
              {asignados.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-32 text-sm text-muted-foreground text-center border border-dashed border-border rounded-md mt-2 p-4">
                  <Building2 className="h-8 w-8 mb-2 opacity-30" />
                  Sin centros asignados: verá todos.
                  <br />
                  Selecciona del panel izquierdo para limitarlo.
                </div>
              ) : (
                <ul className="space-y-1">
                  {asignados.map((c) => {
                    const full = allLaundries.find((l) => String(l._id) === String(c.id));
                    return (
                      <li
                        key={c.id}
                        className="flex items-center justify-between px-3 py-2.5 bg-primary/5 rounded-md border border-primary/20"
                      >
                        <div className="flex flex-col min-w-0">
                          <span className="text-sm font-medium truncate">{c.nombre}</span>
                          {full?.ciudad && (
                            <span className="text-xs text-muted-foreground truncate">{full.ciudad}</span>
                          )}
                        </div>
                        <button
                          onClick={() => handleRemover(c.id)}
                          className="text-red-400 hover:text-red-600 transition-colors shrink-0 ml-2"
                          title="Quitar centro"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2 px-6 py-4 border-t border-border shrink-0">
          <Button variant="outline" onClick={onClose} disabled={isSaving}>
            Cancelar
          </Button>
          <Button onClick={handleGuardar} disabled={isSaving}>
            {isSaving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
            Guardar asignación
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AsignarCentrosUsuarioModal;
